import React, { useState } from 'react';
import { toast } from 'react-toastify';

import SitrepSectionEditDrawer from 'components/DisasterGPT/SitrepSectionEditDrawer';
import { createSectionTemplate } from 'components/DisasterGPT/hooks/useSectionTemplates';
import {
  useRefreshSitrepV2,
  useAddSitrepSectionV2,
  useSitrepSectionAILocks,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import ReportsFileAttachments from 'components/Reports/ReportsFileAttachments';
import ReportsSectionDisplay from 'components/Reports/ReportsSectionDisplay';
import SitrepSectionTabs from 'components/Reports/SitrepSectionTabs';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import RequestApprovalButton from 'components/Reports/RequestApprovalButton';
import ReviewCardActions from 'components/Reports/ReviewCardActions';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import useSitrepSectionActions from 'components/Reports/hooks/useSitrepSectionActions';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';
import {
  SitrepCardSurface,
  type EditableReportsSitrepCardProps,
} from 'components/Reports/reports.types';
import Sentry from '../../instrument';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAppSelector, selectGroupGuid } from 'slices/commonSelectors';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

/**
 * Editable SITREP card used on Draft and Review surfaces.
 *
 * ### Draft surface
 *
 * Full editing is always on: add/remove/reorder sections, AI generate, section
 * settings, file attachments, request approval via the header button. There is
 * intentionally no SITREP-wide Save button — each section is saved individually
 * via ReportsSectionDisplay.
 *
 * ### Review surface
 *
 * Approve / reject is the **primary** workflow (always visible). An explicit
 * **"Edit before approving"** toggle lets reviewers make changes before
 * approval when needed. This is intentionally separated from the draft
 * authoring path — the review edit toggle only enables content editing and
 * section settings, but does NOT expose draft-only controls like "Add Section",
 * AI Generate, or file attachments.
 */
const ReportsSitrepCard: React.FC<EditableReportsSitrepCardProps> = ({
  sitrep,
  surface,
}) => {
  const tabSetId = React.useId();
  const isDraft = surface === SitrepCardSurface.Draft;
  const isReview = surface === SitrepCardSurface.Review;
  // Edit-mode toggle is review-only: reviewers click "Edit before approving"
  // to switch into an edit state. On the Draft surface authoring is always
  // enabled, so this stays false.
  const [isReviewEditMode, setIsReviewEditMode] = useState(false);

  // ── Data & hooks (all at component top-level) ──────────────────────
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, refetch, refreshedAt } = cardData;
  const { activeSectionIndex, activeSection, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);

  const {
    editSettingsSection,
    setEditSettingsSection,
    handleSaveSettings,
    handleDeleteSection,
    handleMoveSection,
    canMoveUp,
    canMoveDown,
  } = useSitrepSectionActions(sitrep.id, sitrepData?.sections, refetch);

  const groupGuid = useAppSelector(selectGroupGuid);
  const addSectionMutation = useAddSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();

  // Poll for per-section AI generation locks so we can
  // disable the AI Generate Draft button when it's in use.
  const shouldPollAILocks = isDraft && activeSection?.type === 'ai';
  const { data: sectionAILocks } = useSitrepSectionAILocks(
    shouldPollAILocks ? sitrep.id : undefined
  );
  const isActiveSectionLocked = activeSection
    ? !!sectionAILocks?.get(activeSection.id)
    : false;

  /**
   * Whether the current surface allows draft-only authoring controls (add
   * section, AI generate, file attachments). Always on for Draft; always off
   * for Review (review editing intentionally excludes these — reviewers can
   * edit existing section content and tweak settings, but cannot restructure
   * the report).
   */
  const canUseDraftAuthoringControls = isDraft;

  // ── Handlers ───────────────────────────────────────────────────────

  const handleAddBlankSection = async () => {
    try {
      const template = await createSectionTemplate(
        {
          name: 'New Section',
          type: 'manual',
          output_format_structured: { summary_options: [] },
        },
        groupGuid
      );
      await addSectionMutation.mutateAsync({
        sitrepId: sitrep.id,
        sectionTemplateId: template.id as UUID,
        selectedDatetime: new Date().toISOString(),
        sitrep_source: sitrep.sitrep_source,
        sitrep_source_id: sitrep.sitrep_source_id,
        order: (sitrepData?.sections?.length ?? 0) + 1,
        aor_id: sitrep.aor_id ?? '',
      });
      const result = await refetch();
      const updatedSections = result.data?.sections;
      if (updatedSections && updatedSections.length > 0) {
        setEditSettingsSection(updatedSections[updatedSections.length - 1]);
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to add section');
    }
  };

  const handleAIGenerateDraft = async () => {
    if (!activeSection) return;
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: [activeSection.id],
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <SitrepCardShell
      sitrep={sitrep}
      surface={surface}
      cardData={cardData}
      headerRight={
        isDraft ? <RequestApprovalButton sitrep={sitrep} /> : undefined
      }
      onCollapse={() => {
        setEditSettingsSection(null);
        setIsReviewEditMode(false);
      }}
    >
      {/* Last-updated timestamp + review-only "Edit before approving" toggle */}
      <div className="d-flex align-items-center justify-content-between">
        {refreshedAt && (
          <small className="text-white">
            Last updated: {refreshedAt} by{' '}
            {sitrep.updated_by_user_name ??
              sitrep.created_by_user_name ??
              'Unknown'}
          </small>
        )}
        {isReview && (
          <StylishNewButton
            className="button--secondary button--icon"
            type="button"
            aria-pressed={isReviewEditMode}
            aria-label={
              isReviewEditMode
                ? 'Done editing before approval'
                : 'Edit before approving'
            }
            title={isReviewEditMode ? 'Done editing' : 'Edit before approving'}
            onClick={() => {
              if (isReviewEditMode) {
                setEditSettingsSection(null);
              }
              setIsReviewEditMode((v) => !v);
            }}
          >
            <SharedIcon
              iconName={isReviewEditMode ? 'check' : 'edit'}
              size="sm"
            />
          </StylishNewButton>
        )}
      </div>

      {/* Section tabs + draft-only add-section button */}
      <div className="d-flex align-items-center justify-content-between">
        {sitrepData?.sections && (
          <SitrepSectionTabs
            sections={sitrepData.sections}
            activeSectionIndex={activeSectionIndex}
            onSectionChange={setActiveSectionIndex}
            className="mb-3"
            idBase={tabSetId}
          />
        )}
        {canUseDraftAuthoringControls && (
          <StylishNewButton
            className={classnames('button--secondary', {
              'button--icon': sitrepData?.sections?.length,
            })}
            type="button"
            aria-label="Add new section"
            title="Add new section"
            disabled={addSectionMutation.isPending}
            onClick={handleAddBlankSection}
          >
            <SharedIcon iconName="add" size="sm" />
            {!sitrepData?.sections?.length && (
              <span className="ms-3">Add First Section</span>
            )}
          </StylishNewButton>
        )}
      </div>

      {/* Draft-only: AI generate + section settings toolbar */}
      {canUseDraftAuthoringControls && activeSection && (
        <div className="d-flex align-items-center gap-2 mb-3">
          {activeSection.type === 'ai' && (
            <OverlayTrigger
              placement="top"
              overlay={
                isActiveSectionLocked ? (
                  <Tooltip id="ai-locked-tooltip">
                    Someone else is editing this section right now. Wait until
                    they&apos;re finished and you can AI generate a draft.
                  </Tooltip>
                ) : (
                  <></>
                )
              }
            >
              <span>
                <StylishNewButton
                  className="button--secondary"
                  type="button"
                  disabled={
                    refreshSitrepMutation.isPending || isActiveSectionLocked
                  }
                  onClick={handleAIGenerateDraft}
                  style={
                    isActiveSectionLocked
                      ? { pointerEvents: 'none' }
                      : undefined
                  }
                >
                  <SharedIcon
                    iconName="auto_awesome"
                    size="sm"
                    classes={`me-1${refreshSitrepMutation.isPending ? ' spinning' : ''}`}
                  />
                  {refreshSitrepMutation.isPending
                    ? 'Generating...'
                    : 'AI Generate Draft'}
                </StylishNewButton>
              </span>
            </OverlayTrigger>
          )}
          <StylishNewButton
            className="button--secondary button"
            type="button"
            onClick={() => setEditSettingsSection(activeSection)}
          >
            <SharedIcon iconName="settings" size="sm" classes="me-1" />
            Section Settings
          </StylishNewButton>
        </div>
      )}

      {/* Review-edit: section settings only (no add/AI) */}
      {isReview && isReviewEditMode && activeSection && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <StylishNewButton
            className="button--secondary button"
            type="button"
            onClick={() => setEditSettingsSection(activeSection)}
          >
            <SharedIcon iconName="settings" size="sm" classes="me-1" />
            Section Settings
          </StylishNewButton>
        </div>
      )}

      {/* Active section content. Per-section Edit/Save/Cancel lives inside
          ReportsSectionDisplay — there is intentionally no SITREP-wide Save. */}
      {activeSection && (
        <div
          id={`${tabSetId}-panel-${activeSectionIndex}`}
          role="tabpanel"
          aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
          className="reports-card__section-content"
        >
          <ReportsSectionDisplay
            key={activeSection.id}
            section={activeSection}
            sitrepId={sitrep.id}
            onUpdated={() => refetch()}
            canEdit={isDraft || isReviewEditMode}
          />
        </div>
      )}

      {/* Draft-only: file attachments */}
      {canUseDraftAuthoringControls && (
        <div className="mt-3">
          <ReportsFileAttachments
            sitrepId={sitrep.id}
            workspaceId={sitrep.incident_id}
          />
        </div>
      )}

      {/* Review surface: approve/reject always visible; Done Editing inline when in edit mode */}
      {isReview && (
        <ReviewCardActions
          sitrep={sitrep}
          isEditMode={isReviewEditMode}
          onDoneEditing={
            isReviewEditMode
              ? () => {
                  setEditSettingsSection(null);
                  setIsReviewEditMode(false);
                  refetch();
                }
              : undefined
          }
        />
      )}

      {/* Section settings drawer */}
      {editSettingsSection && (
        <SitrepSectionEditDrawer
          onHide={() => setEditSettingsSection(null)}
          onSave={handleSaveSettings}
          onDelete={() => handleDeleteSection(editSettingsSection)}
          onMoveUp={() => handleMoveSection('up')}
          onMoveDown={() => handleMoveSection('down')}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          section={editSettingsSection}
          sitrepId={sitrep.id}
        />
      )}
    </SitrepCardShell>
  );
};

export default ReportsSitrepCard;
